import { useState } from 'react';
import type { FC, FormEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MarketingLayout from '../components/MarketingLayout';
import { useTranslation } from '../i18n/I18nContext';
import { Spinner } from '../components/ui/spinner';

const AuthReset: FC = () => {
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { refreshSession } = useAuth();

  const { email } = (location.state as { email?: string }) || {};

  if (!email) {
    return <Navigate to="/auth/email" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsResetting(true);

    try {
      const { confirmResetPassword, signIn } = await import('aws-amplify/auth');
      await confirmResetPassword({
        username: email,
        confirmationCode: code.trim(),
        newPassword,
      });

      // Auto-sign-in with new password (same pattern as AuthVerify)
      try {
        await signIn({ username: email, password: newPassword });
      } catch (signInErr: any) {
        if (signInErr?.name !== 'UserAlreadyAuthenticatedException') {
          throw signInErr;
        }
      }

      await refreshSession();
      navigate('/articles', { replace: true });
    } catch (err: any) {
      const errorName = err?.name || '';

      if (errorName === 'CodeMismatchException') {
        setError(t('auth.invalid_code'));
      } else if (errorName === 'ExpiredCodeException') {
        setError(t('auth.code_expired'));
      } else if (errorName === 'LimitExceededException') {
        setError(t('auth.too_many_attempts'));
      } else {
        setError(err instanceof Error ? err.message : t('auth.error_generic'));
      }
      setIsResetting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResendMessage('');

    try {
      const { resetPassword } = await import('aws-amplify/auth');
      await resetPassword({ username: email });
      setResendMessage(t('auth.reset_code_sent', { email }));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.error_generic'));
    }
  };

  return (
    <MarketingLayout>
      <div className="max-w-sm mx-auto px-4 pt-8 pb-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {t('auth.reset_heading')}
          </h1>
          <p className="text-muted-foreground">
            {t('auth.reset_description', { email })}
          </p>
        </div>

        {error && (
          <div className="bg-destructive text-destructive-foreground rounded-md p-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {resendMessage && (
          <div className="bg-success/10 text-success rounded-md p-3 mb-4 text-sm">
            {resendMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('auth.reset_code_label')}</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={isResetting}
              required
              autoFocus
              autoComplete="one-time-code"
              placeholder="000000"
              className="w-full border border-input rounded-md px-3 py-2.5 bg-background text-foreground text-sm text-center text-2xl tracking-[0.3em] font-mono"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('auth.new_password_label')}</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isResetting}
              required
              minLength={8}
              autoComplete="new-password"
              className="w-full border border-input rounded-md px-3 py-2.5 bg-background text-foreground text-sm"
            />
          </div>

          <button
            type="submit"
            disabled={isResetting || code.length < 6 || newPassword.length < 8}
            className="w-full bg-success text-success-foreground px-6 py-3 rounded-lg text-base font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {isResetting ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner size="sm" className="border-success-foreground" />
                {t('auth.resetting')}
              </span>
            ) : (
              t('auth.reset_button')
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={handleResend}
            disabled={isResetting}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('auth.resend_code')}
          </button>
        </div>
      </div>
    </MarketingLayout>
  );
};

export default AuthReset;
